import classNames from "classnames";
import first from "lodash/first";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";

import { BooleanCustomValueField } from "./boolean_custom_value_field/boolean_custom_value_field";
import { CalendarCustomValueField } from "./calendar_custom_value_field/calendar_custom_value_field";
import { createCustomValueChangeHandlers } from "./custom_value_change_handlers";
import { createCustomValueOptionsFetcher } from "./custom_value_options_fetcher";
import css from "./custom_values_edit_field.module.scss";
import { CustomValuesEditFieldProps } from "./custom_values_edit_field.types";
import { SelectCustomValueField } from "./select_custom_value_field/select_custom_value_field";
import { useCommonProps } from "./use_common_props";
import { useCustomValueFieldFlags } from "./use_custom_value_field_flags";

export type { CustomValuesEditFieldProps } from "./custom_values_edit_field.types";

export const CustomValuesEditField = ({
  kind,
  position,
  customType,
  errors,
  adminPage,
  profile,
  label,
  title,
  placeholder,
  tip,
  required,
  isActivity,
  activityCreator,
  forceUnlocked,
  perPage = 250,
  multi,
  values = [],
  qaIdPrefix,
  letClear,
  disabled,
  letCreate = null,
  hasValuesWithExpiryDate,
  classes = {},
  parseOptions,
  createOptionBlocklist,
  renderDisplayValues,
  displayValuesBelow,
  handleChange,
  useGlobalValues,
  requiresSearch,
  connectedValuesIds = [],
  portalRef,
  onRestore,
  restoreLabel,
  hideLabel,
  hideTip,
  variant = "default",
  dropdownWidth,
  focusSearchInputOnOpen,
  tabIndex,
  style,
  inputRef
}: CustomValuesEditFieldProps) => {
  const { formatISODate } = useDateFormatter();
  const commonProps = useCommonProps({
    adminPage,
    customType,
    profile,
    position,
    isActivity,
    activityCreator,
    forceUnlocked,
    errors,
    placeholder,
    tip: hideTip ? "" : tip,
    label: hideLabel ? "" : label,
    title,
    kind,
    disabled,
    required,
    variant,
    tabIndex,
    style
  });

  const { isMulti, isCalendar, isBoolean, finalLetCreate, isChecked, hasExpiryDate } =
    useCustomValueFieldFlags({
      customType,
      multi,
      values,
      letCreate,
      hasValuesWithExpiryDate
    });

  const fetchOptions = createCustomValueOptionsFetcher({
    customType,
    values,
    perPage,
    useGlobalValues,
    connectedValuesIds,
    requiresSearch
  });

  const { handleBooleanChange, handleCalendarChange, handleExpiryDateChange, handleCreate } =
    createCustomValueChangeHandlers({ values, isMulti, formatISODate, handleChange });

  const fieldQaId = `${qaIdPrefix}-${customType?.name}`;

  return (
    <div
      className={classNames(classes.root, { [css.tableVariant]: variant === "table" })}
      key={customType?.id}
      data-qa-id={fieldQaId}
    >
      {isBoolean ? (
        <BooleanCustomValueField
          commonProps={commonProps}
          isChecked={isChecked}
          onChange={handleBooleanChange}
          qaId={fieldQaId}
          label={label}
          switchRef={inputRef}
        />
      ) : isCalendar ? (
        <CalendarCustomValueField
          commonProps={commonProps}
          selectedDate={(first(values)?.value as string) ?? null}
          onChange={handleCalendarChange}
          portalRef={portalRef}
          letClear={letClear}
          inputRef={inputRef}
          inputClassName={classes.calendarInput}
        />
      ) : (
        <SelectCustomValueField
          commonProps={commonProps}
          values={values}
          isMulti={isMulti}
          handleChange={handleChange}
          config={{
            customType,
            connectedValuesIds,
            hasExpiryDate,
            finalLetCreate,
            fetchOptions,
            handleExpiryDateChange,
            handleCreate,
            displayValuesBelow,
            onRestore,
            letClear,
            createOptionBlocklist,
            restoreLabel,
            renderDisplayValues,
            disabled,
            parseOptions,
            portalRef,
            variant,
            dropdownWidth,
            focusSearchInputOnOpen,
            inputRef
          }}
        />
      )}
    </div>
  );
};
